const intersects = (a, b) => a.minX <= b.maxX && a.maxX >= b.minX && a.minZ <= b.maxZ && a.maxZ >= b.minZ;
const union = entries => entries.reduce((b, entry) => ({ minX: Math.min(b.minX, entry.bounds.minX),
  minZ: Math.min(b.minZ, entry.bounds.minZ), maxX: Math.max(b.maxX, entry.bounds.maxX), maxZ: Math.max(b.maxZ, entry.bounds.maxZ) }),
{ minX: Infinity, minZ: Infinity, maxX: -Infinity, maxZ: -Infinity });

/** Static balanced AABB tree. Rebuilt for a new scene, never for a camera move. */
export function createViewportSpatialIndex(entries) {
  if (!Array.isArray(entries) || entries.length > 100000) throw new RangeError('Invalid spatial index size');
  const uniqueIds = new Set();
  const detached = entries.map(({ id, bounds }) => {
    if (typeof id !== 'string' || uniqueIds.has(id) || !bounds || ![bounds.minX, bounds.minZ, bounds.maxX, bounds.maxZ].every(Number.isFinite)
      || bounds.minX > bounds.maxX || bounds.minZ > bounds.maxZ) throw new TypeError('Invalid spatial entry');
    uniqueIds.add(id); return { id, bounds: { ...bounds } };
  });
  const build = items => {
    if (!items.length) return null;
    const bounds = union(items);
    if (items.length <= 8) return { bounds, items };
    const axis = bounds.maxX - bounds.minX >= bounds.maxZ - bounds.minZ ? 'X' : 'Z';
    items.sort((a, b) => (a.bounds[`min${axis}`] + a.bounds[`max${axis}`]) - (b.bounds[`min${axis}`] + b.bounds[`max${axis}`]));
    const middle = Math.floor(items.length / 2);
    return { bounds, left: build(items.slice(0, middle)), right: build(items.slice(middle)) };
  };
  let root = build(detached);
  return {
    query(bounds) {
      if (!bounds || ![bounds.minX, bounds.minZ, bounds.maxX, bounds.maxZ].every(Number.isFinite)
        || bounds.minX > bounds.maxX || bounds.minZ > bounds.maxZ) throw new RangeError('Invalid query');
      const ids = []; let visited = 0;
      const visit = node => {
        if (!node) return;
        visited++;
        if (!intersects(node.bounds, bounds)) return;
        if (node.items) { for (const item of node.items) if (intersects(item.bounds, bounds)) ids.push(item.id); }
        else { visit(node.left); visit(node.right); }
      };
      visit(root); return { ids, visited };
    },
    get bounds() { return root ? { ...root.bounds } : null; },
    dispose() { root = null; }
  };
}
